import React from 'react';

import {
    Box, Button, Divider, Flex, Text
} from 'components/ui';
import Modal from 'components/Modal/Modal';
import ProductImage from 'components/Product/ProductImage/ProductImage';

import { getImageAltText } from 'utils/Accessibility';
import dateUtils from 'utils/Date';
import formValidator from 'utils/FormValidator';
import { wrapFunctionalComponent } from 'utils/framework';

import { modal } from 'style/config';

const MAX_LIST_ITEMS = 5;

function AddressChangedModal(props) {
    const {
        isOpen = false, onDismiss = null, currentSubscription, selectedAddress = null, localization = {}, updatedSubscriptions = []
    } = props;

    if (!selectedAddress || !currentSubscription?.subscriptionId) {
        return null;
    }

    const phoneNumber = formValidator.getFormattedPhoneNumber(selectedAddress?.phone);

    // updatedSubscriptionsItems max 5 total items to be displayed in modal
    const updatedSubscriptionsItems = updatedSubscriptions
        .flatMap(sub =>
            (sub?.items ?? []).map(item => ({
                ...item,
                nextShipmentDate: sub?.nextScheduleRunDate ? dateUtils.getPromiseDate(`${sub.nextScheduleRunDate}T00:00`) : ''
            }))
        )
        .slice(0, MAX_LIST_ITEMS);

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            hasBodyScroll={true}
            width={0}
            focusDialog={true}
            isDrawer={true}
        >
            <Modal.Header>
                <Modal.Title>{localization.modalTitle}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Flex
                    flexDirection='column'
                    gap={4}
                >
                    <Text
                        is='p'
                        children={localization.message}
                    />
                    <Divider marginX={modal.outdentX} />
                    {updatedSubscriptionsItems.map((item, index) => (
                        <React.Fragment key={item?.skuId ?? `subscription-item-${index + 1}`}>
                            <Flex
                                gap={3}
                                alignItems='center'
                            >
                                <Box width={112}>
                                    <ProductImage
                                        id={item?.skuId}
                                        size={80}
                                        skuImages={item?.skuImages}
                                        disableLazyLoad={true}
                                        altText={getImageAltText(item)}
                                    />
                                </Box>
                                <Box>
                                    <Text
                                        is='h4'
                                        fontWeight='bold'
                                        lineHeight='18px'
                                        children={item?.brandName}
                                        data-at={Sephora.debug.dataAt('sku_brand')}
                                    />
                                    <Text
                                        is='p'
                                        children={item?.productName}
                                        data-at={Sephora.debug.dataAt('sku_name')}
                                    />
                                    <Text
                                        is='p'
                                        marginTop={2}
                                        data-at={Sephora.debug.dataAt('sku_next_shipment')}
                                    >
                                        {localization.nextShipment}{' '}
                                        <Text
                                            color='green'
                                            fontWeight='bold'
                                            children={item?.nextShipmentDate}
                                        />
                                    </Text>
                                </Box>
                            </Flex>
                            <Divider marginX={modal.outdentX} />
                        </React.Fragment>
                    ))}
                    <Flex
                        gap={3}
                        alignItems='center'
                    >
                        <Box width={112}>
                            <Text
                                is='h4'
                                fontWeight='bold'
                                lineHeight='18px'
                                children={localization.shippingAddress}
                                data-at={Sephora.debug.dataAt('shipping_address_label')}
                            />
                        </Box>
                        <Box lineHeight='18px'>
                            <Text
                                is='h4'
                                fontWeight='bold'
                                lineHeight='18px'
                                children={`${selectedAddress?.firstName} ${selectedAddress?.lastName}`}
                                data-at={Sephora.debug.dataAt('shipping_address_name')}
                            />
                            <Text
                                is='p'
                                children={selectedAddress?.address1}
                                data-at={Sephora.debug.dataAt('shipping_address_line1')}
                            />
                            <Text
                                is='p'
                                children={`${selectedAddress?.city}, ${selectedAddress?.state} ${selectedAddress?.postalCode}`}
                                data-at={Sephora.debug.dataAt('shipping_address_line2')}
                            />
                            <Text
                                is='p'
                                children={phoneNumber}
                                data-at={Sephora.debug.dataAt('shipping_address_phone')}
                            />
                        </Box>
                    </Flex>
                </Flex>
            </Modal.Body>

            <Modal.Footer hasBorder={true}>
                <Button
                    variant='primary'
                    width='100%'
                    data-at={Sephora.debug.dataAt('address_edit_confirm')}
                    onClick={onDismiss}
                    children={localization.gotIt}
                />
            </Modal.Footer>
        </Modal>
    );
}

export default wrapFunctionalComponent(AddressChangedModal, 'AddressChangedModal');
